import { Helmet } from "react-helmet-async";
import { useEffect, useState, useRef } from "react";
import { Typography, Box, Grid, useTheme } from "@mui/material";
import { APP_TITLE } from "../../constant";
import CandidateInfoCard from "../../modules/ViewRecording/CandidateInfoCard";
import VideoPlayerCard from "../../modules/ViewRecording/VideoPlayerCard";
import InterviewQuestionsCard from "../../modules/ViewRecording/InterviewQuestionsCard";
import { format } from "date-fns";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { ArrowLeft } from "../../components/SvgIcons/SvgIcons";
import { AxiosService } from "../../services/axios/axiosService";
// Helper to format seconds as M:SS (e.g. 3:45)
const formatDuration = (seconds) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return "-";
  const total = Math.max(0, Math.floor(Number(seconds)));
  const mins = Math.floor(total / 60);
  const secs = (total % 60).toString().padStart(2, "0");
  return `${mins}:${secs}`;
};

// Determine whether a response should be counted as "answered"
// (mirrors logic in InterviewQuestionsCard/getStatusMeta).
const isResponseAnswered = (response) => {
  if (!response) return false;
  const duration = response.responseDurationSeconds ?? 0;
  const text = response.transcribedText || "";
  const isNoSpeech =
    typeof text === "string" &&
    /(No speech could be recognized|No speech|Recognition timeout)/i.test(
      text,
    );
  const hasSpeech = !isNoSpeech && text.trim().length > 0;
  return duration > 0 && hasSpeech;
};

const ViewRecording = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { id: interviewIdFromUrl } = useParams();
  const [candidateData, setCandidateData] = useState(state?.candidate || null);
  // If we arrived via in-app navigation, state.candidate will be present.
  // Shared/public links generally have no navigation state.
  const canGoBack = !!state?.candidate;
  const showBackArrow = canGoBack;

  const [detailsData, setDetailsData] = useState({ interview: null, responses: [] });
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionsError, setQuestionsError] = useState(null);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [ttsAutoPlayKey, setTtsAutoPlayKey] = useState(0);

  // Coordination between TTS and video:
  // - When TTS starts, pause the video
  // - When video starts playing, stop TTS
  const videoPlayerRef = useRef(null);
  const stopTtsRef = useRef(null);

  const candidateInfo = {
    name: candidateData?.candidateName || detailsData.interview?.candidateName || "-",
    email: candidateData?.candidateEmail || detailsData.interview?.candidateEmail || "-",
    position: candidateData?.jobTitle || detailsData.interview?.jobTitle || "-",
    interviewId: candidateData?.id || detailsData.interview?.id || "-",
  };

  const rawZohoStatus = (candidateData?.zohoSyncStatus || "").trim();

  const syncStatusData = {
    // Display string: fall back to "Not Synced" when there is no raw status
    status: rawZohoStatus || "Not Synced",
    timestamp: candidateData?.zohoSyncedAt
      ? format(new Date(candidateData.zohoSyncedAt), "MM/dd/yyyy")
      : "",
  };

  // Color rules (header chip):
  // - "Success"  -> green
  // - "Failed"   -> red
  // - "" (empty) or anything else -> orange
  const zohoIsSuccess = rawZohoStatus === "Success";
  const zohoIsFailed = rawZohoStatus === "Failed";
  const zohoDotColor = zohoIsSuccess
    ? "#059669"
    : zohoIsFailed
      ? "#ef4444"
      : "#f97316";
  const zohoBgColor = zohoIsSuccess
    ? "#a8f2c18f"
    : zohoIsFailed
      ? "rgba(248, 113, 113, 0.12)"
      : "rgba(249, 115, 22, 0.12)";
  const zohoTextColor = zohoDotColor;

  // Figure out which response is active (selected question), or default to
  // the first response when nothing is selected.
  const activeResponse =
    selectedResponse || detailsData.responses?.[0] || null;
  const totalQuestions =
    detailsData.interview?.totalQuestions ?? detailsData.responses?.length ?? 0;
  const answeredCount = Array.isArray(detailsData.responses)
    ? detailsData.responses.filter(isResponseAnswered).length
    : 0;

  // Total duration across all recorded questions (sum of each response's duration)
  const totalDurationSeconds = Array.isArray(detailsData.responses)
    ? detailsData.responses.reduce(
        (sum, r) => sum + (r?.responseDurationSeconds ?? 0),
        0,
      )
    : 0;

  // AI scores to display:
  // - For a specific question (activeResponse), use behavioralScore/contentScore
  // - Fallback to overall candidate interview scores (behaviourScore/answereScore)
  const questionScores = activeResponse
    ? {
        behaviorScore:
          activeResponse.behaviorScore ??
          activeResponse.behaviourScore ??
          activeResponse.behavioralScore ??
          null,
        aiResponse:
          activeResponse.aiResponse ??
          activeResponse.answereScore ??
          activeResponse.contentScore ??
          null,
        contentFeedback:
          activeResponse.contentFeedback ??
          null,
          behavioralFeedback:
          activeResponse.behavioralNotes ??
          null,
      }
    : null;

  const overallScores = candidateData
    ? {
        behaviorScore:
          candidateData.behaviorScore ??
          candidateData.behaviourScore ??
          candidateData.behavioralScore ??
          null,
        aiResponse:
          candidateData.aiResponse ??
          candidateData.answereScore ??
          candidateData.contentScore ??
          null,
      }
    : null;

  const scoresData = questionScores || overallScores || {};

  const totalBehaviourScore =
    candidateData?.behaviourScore ??
    candidateData?.behaviorScore ??
    candidateData?.behavioralScore;
  const totalAiScore =
    candidateData?.aiResponse ??
    candidateData?.answereScore ??
    candidateData?.contentScore;

  const interviewDetails = {
    date: candidateData?.completedAt
      ? format(new Date(candidateData.completedAt), "MM/dd/yyyy")
      : "-",
    duration: formatDuration(totalDurationSeconds),
    questionsAnswered:
      totalQuestions > 0
        ? `${answeredCount} of ${totalQuestions}`
        : answeredCount > 0
          ? `${answeredCount} answered`
          : "0 of 0",
    totalBehaviourScore:
      totalBehaviourScore != null
        ? Math.floor(Number(totalBehaviourScore) * 10) / 10
        : null,
    totalAiScore:
      totalAiScore != null ? Math.floor(Number(totalAiScore) * 10) / 10 : null,
    interviewId: candidateData?.id || interviewIdFromUrl || "-",
  };

  const resolvedInterviewId = candidateData?.id || interviewIdFromUrl;

  // Text to show under the video player.
  // When there are no responses at all, show an explicit "no question" message
  // instead of leaving this area blank.
  const videoQuestionText =
    selectedResponse?.question?.questionText ||
    (!questionsLoading &&
      !questionsError &&
      (detailsData.responses?.length ?? 0) === 0
      ? "No question details available."
      : "");

  const transcriptText =
    activeResponse?.transcribedText && typeof activeResponse.transcribedText === "string"
      ? activeResponse.transcribedText
      : "";
  const hasVideoForActive =
    !!activeResponse?.videoBlobUrl &&
    typeof activeResponse.videoBlobUrl === "string" &&
    activeResponse.videoBlobUrl.trim() !== "";

  // When TTS starts (question audio), pause any playing video
  const handleTtsStart = () => {
    const api = videoPlayerRef.current;
    if (api && typeof api.pause === "function") {
      api.pause();
    }
  };

  // When video starts playing, stop any TTS audio
  const handleVideoPlay = () => {
    if (typeof stopTtsRef.current === "function") {
      stopTtsRef.current();
    }
  };

  useEffect(() => {
    const interviewId = resolvedInterviewId;
    if (!interviewId) return;

    const fetchInterviewDetails = async () => {
      try {
        setQuestionsLoading(true);
        setQuestionsError(null);

        const response = await AxiosService.get(
          `/api/Interview/${interviewId}/details`,
        );

        const responses = response?.responses ?? [];
        setDetailsData({
          interview: response?.interview ?? null,
          responses,
        });

        // If opened directly via URL (no navigation state),
        // populate candidateData from the API response.
        if (!candidateData && response?.interview) {
          setCandidateData(response.interview);
        }

        if (responses.length > 0) {
          setSelectedResponse(responses[0]);
        } else {
          setSelectedResponse(null);
        }
      } catch (err) {
        setQuestionsError(
          err?.message || "Failed to load interview questions.",
        );
      } finally {
        setQuestionsLoading(false);
      }
    };

    fetchInterviewDetails();
  }, [resolvedInterviewId]);

  // Shared-link mode (no navigation state):
  // when user clicks browser Back, refresh this same page.
  useEffect(() => {
    if (canGoBack) return;

    const currentUrl = window.location.href;

    // Add one extra entry so the first Back triggers `popstate` while still on this page.
    window.history.pushState(null, "", currentUrl);

    const handlePopState = () => {
      // Keep the user on the same URL and refresh the page content.
      window.history.pushState(null, "", currentUrl);
      window.location.reload();
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [canGoBack]);

  return (
    <>
      <Helmet>
        <title>Interview Recording | {APP_TITLE}</title>
        <meta
          name="Interview Recording"
          content="Review candidate interview and AI-generated scores"
        />
      </Helmet>

      <Box
        sx={{
          p: 4,
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 0.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {showBackArrow && (
                <Box
                  onClick={() => {
                    navigate(-1);
                  }}
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    padding: "8px",
                    borderRadius: "8px",
                    transition: "all 0.2s",
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "#f3f4f6",
                    },
                  }}
                >
                  <ArrowLeft size={24} color={theme.palette.text.primary} />
                </Box>
              )}
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: theme.palette.text.primary }}
              >
                IGNA Scout Recording
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              {syncStatusData.status && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 2.5,
                    py: 1,
                    borderRadius: 6,
                    backgroundColor: zohoBgColor,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: zohoDotColor,
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: 0.25,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        //color: zohoTextColor,
                      }}
                    >
                      Zoho Sync: <span className="fw-500" style={{ color: zohoTextColor, fontWeight: 600, fontSize: 14 }}>{syncStatusData.status}</span>
                    </Typography>
                    {/* {syncStatusData.timestamp && (
                      <Typography
                        variant="caption"
                        //sx={{ color: zohoTextColor }}
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        <span className="fw-500" >Synced at:</span>{" "}
                        <span className="fw-500" style={{ color: zohoTextColor}}>{syncStatusData.timestamp || ""}</span>
                      </Typography>
                    )} */}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      
        <Grid container spacing={{ xs: 2, md: 3 }} alignItems="stretch">
          {/* Candidate & Interview Information full width on top */}
          <Grid size={{ xs: 12 }} sx={{ minWidth: 0 }}>
            <CandidateInfoCard candidate={candidateInfo} details={interviewDetails} />
          </Grid>

          {/* Video + details section */}
          <Grid size={{ xs: 12, md: 6, sm: 12, lg: 6 }} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                minWidth: 0,
              }}
            >
              <Box sx={{ flex: 1, mb: 2 }}>
                <VideoPlayerCard
                  interview={interviewDetails}
                  ref={videoPlayerRef}
                  videoBlobUrl={activeResponse?.videoBlobUrl}
                  questionText={videoQuestionText}
                  transcriptText={transcriptText}
                  autoPlayKey={ttsAutoPlayKey}
                  onVideoPlay={handleVideoPlay}
                  loading={questionsLoading}
                  showNoVideo={!questionsLoading && !questionsError && !hasVideoForActive}
                />
              </Box>
             
            </Box>
          </Grid>
          {/* Questions section */}
          <Grid size={{ xs: 12, md: 6, lg: 6 }} sx={{ display: "flex", minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                minWidth: 0,
              }}
            >
              <InterviewQuestionsCard
                loading={questionsLoading}
                error={questionsError}
                questions={detailsData.responses}
                totalQuestions={detailsData.interview?.totalQuestions}
                selectedResponseId={selectedResponse?.id}
                onSelectResponse={setSelectedResponse}
                onTtsEnd={() => setTtsAutoPlayKey((k) => k + 1)}
                onTtsStart={handleTtsStart}
                registerStopTts={(fn) => {
                  stopTtsRef.current = fn;
                }}
              />
            </Box>
          </Grid>

         
         
        </Grid>
      </Box>
    </>
  );
};

export default ViewRecording;
